import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Html } from "@react-three/drei";
import { invalidate, useFrame } from "@react-three/fiber";
import { useDispatch, useSelector } from "react-redux";
import { Color, Object3D, Vector3 } from "three";
import {
  selectSoundEmissionPoint,
  setSoundEmissionApiLrMax,
  setSoundEmissionValueRange,
} from "../../redux/soundEmission";
import { useColorBar } from "../CustomHooks";
import {
  filterObjectsByColorbarRange,
  formatHeatmapValue,
  getRangeModeDisplayColor,
  sensorPlacementScale,
} from "../utils";
import { HEATMAP_LABEL_LAYER } from "./heatmapLabelLayers";
import { SphereInstancedMesh } from "./InstancedMesh";

const LINK_COLOR = "#3d6fe8";
const LINK_HIGHLIGHT_COLOR = "#ffb000";
const PULSE_COLOR = "#ffd84a";
const RECEIVER_COLD_COLOR = new Color("#00f");
const RECEIVER_HOT_COLOR = new Color("#d61f1f");
const PULSES_PER_LINK = 2;
const PULSE_SPEED = 0.32;
const pulseColor = new Color(PULSE_COLOR);
const pulseHighlightColor = new Color("#fff176");
const pulseDummy = new Object3D();
const receiverPressureColor = new Color();

const getReceiverPressureColor = (pressureRatio) =>
  receiverPressureColor
    .copy(RECEIVER_COLD_COLOR)
    .lerp(RECEIVER_HOT_COLOR, pressureRatio)
    .clone();

const getPointPosition = (point) => [point.x, point.y, point.z];
const getAcousticValue = (point) => point.max_allowable_lw;
const disableRaycast = () => null;
const toNumber = (value) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : 0;
};

const getLimitingReceiver = (point, receivers) => {
  if (!point) return null;

  return receivers.reduce((closest, receiver) => {
    const distance =
      (receiver.x - point.di_receiver_x) ** 2 +
      (receiver.y - point.di_receiver_y) ** 2 +
      (receiver.z - point.di_receiver_z) ** 2;

    return !closest || distance < closest.distance
      ? { receiver, distance }
      : closest;
  }, null)?.receiver;
};

const getReceiverLinks = (points, highlightedPointId) =>
  points
    .map((point) => {
      const origin = [point.x, point.y, point.z];
      const target = [
        toNumber(point.di_receiver_x),
        toNumber(point.di_receiver_y),
        toNumber(point.di_receiver_z),
      ];
      const direction = [
        target[0] - origin[0],
        target[1] - origin[1],
        target[2] - origin[2],
      ];
      const length = Math.hypot(...direction);
      if (!length) return null;

      const highlighted = point.id === highlightedPointId;
      return {
        id: point.id,
        origin,
        direction: direction.map((value) => value / length),
        length,
        highlighted,
        color: highlighted ? LINK_HIGHLIGHT_COLOR : LINK_COLOR,
        headLength: highlighted ? 1.6 : 1.1,
        headWidth: highlighted ? 0.7 : 0.45,
      };
    })
    .filter(Boolean);

const SoundEmissionReceivers = ({
  receivers,
  highlightedReceiver,
  pressures,
}) => {
  const maxPressure = Math.max(1, ...(pressures ?? [0]));

  return (
    <group rotation-x={Math.PI * -0.5}>
      {receivers.map((receiver, index) => {
        const isHighlighted = receiver === highlightedReceiver;
        const pressure = pressures?.[index] ?? 0;
        const pressureRatio = pressure / maxPressure;
        const pressureColor = getReceiverPressureColor(pressureRatio);
        const config = isHighlighted
          ? { scale: 1.35, color: LINK_HIGHLIGHT_COLOR }
          : { scale: 1, color: pressureColor };

        return (
          <group
            key={index}
            position={[receiver.x, receiver.y, receiver.z]}
            scale={config.scale}
            userData={{ excludeFromLabelOcclusion: true }}
          >
            <mesh raycast={disableRaycast}>
              <boxGeometry args={[0.8, 0.8, 0.8]} />
              <meshBasicMaterial color={config.color} />
            </mesh>
            {pressure > 0 && (
              <mesh scale={1.6 + pressureRatio * 2.4} raycast={disableRaycast}>
                <sphereGeometry args={[0.7, 20, 14]} />
                <meshBasicMaterial
                  color={isHighlighted ? LINK_HIGHLIGHT_COLOR : pressureColor}
                  transparent
                  opacity={isHighlighted ? 0.36 : 0.1 + pressureRatio * 0.26}
                  depthWrite={false}
                />
              </mesh>
            )}
          </group>
        );
      })}
    </group>
  );
};

const SoundEmissionLinkPulses = ({ links }) => {
  const meshRef = useRef();
  const pulseCount = links.length * PULSES_PER_LINK;

  useFrame(({ clock }) => {
    const mesh = meshRef.current;
    if (!mesh || !pulseCount) return;

    let instanceIndex = 0;
    links.forEach((link, linkIndex) => {
      for (let pulse = 0; pulse < PULSES_PER_LINK; pulse += 1) {
        const travel =
          (clock.elapsedTime * PULSE_SPEED + linkIndex * 0.13 + pulse * 0.5) %
          1;
        pulseDummy.position.set(
          link.origin[0] + link.direction[0] * link.length * travel,
          link.origin[1] + link.direction[1] * link.length * travel,
          link.origin[2] + link.direction[2] * link.length * travel,
        );
        const scale = link.highlighted ? 1.55 : 1;
        pulseDummy.scale.set(scale, scale, scale);
        pulseDummy.updateMatrix();
        mesh.setMatrixAt(instanceIndex, pulseDummy.matrix);
        mesh.setColorAt(
          instanceIndex,
          link.highlighted ? pulseHighlightColor : pulseColor,
        );
        instanceIndex += 1;
      }
    });

    mesh.instanceMatrix.needsUpdate = true;
    if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
    invalidate();
  });

  if (!pulseCount) return null;

  return (
    <instancedMesh
      key={pulseCount}
      ref={meshRef}
      args={[null, null, pulseCount]}
      raycast={disableRaycast}
    >
      <sphereGeometry args={[0.15, 10, 8]} />
      <meshBasicMaterial />
    </instancedMesh>
  );
};

const SoundEmissionReceiverLinks = ({ points, highlightedPointId }) => {
  const links = useMemo(
    () => getReceiverLinks(points, highlightedPointId),
    [highlightedPointId, points],
  );

  return (
    <group
      rotation-x={Math.PI * -0.5}
      raycast={disableRaycast}
      userData={{ excludeFromLabelOcclusion: true }}
    >
      {links.map((link) => (
        <arrowHelper
          key={`${link.id}-${link.highlighted}`}
          args={[
            new Vector3(...link.direction),
            new Vector3(...link.origin),
            link.length,
            link.color,
            link.headLength,
            link.headWidth,
          ]}
        />
      ))}
      <SoundEmissionLinkPulses links={links} />
    </group>
  );
};

const SoundEmissionValueLabels = ({ points }) => {
  const enabled = useSelector((state) => state.sceneVar.heatmapLabelsEnabled);
  const sizePercent = useSelector(
    (state) => state.sceneVar.heatmapLabelsSizePercent,
  );
  const digits = useSelector(
    (state) =>
      state.sceneVar.heatmapLabelDigitsByLayer?.[
        HEATMAP_LABEL_LAYER.SOUND_EMISSION
      ] ?? 1,
  );

  if (!enabled) return null;

  return (
    <group rotation-x={Math.PI * -0.5}>
      {points.map((point) => (
        <Html
          key={point.id}
          position={[point.x, point.y, point.z + 0.65]}
          center
          style={{ pointerEvents: "none" }}
        >
          <span
            style={{
              display: "block",
              color: "#111",
              fontSize: `${Math.max(8, 12 * (sizePercent / 100))}px`,
              fontWeight: 700,
              lineHeight: 1,
              textShadow:
                "-1px -1px 0 #fff, 1px -1px 0 #fff, -1px 1px 0 #fff, 1px 1px 0 #fff",
              whiteSpace: "nowrap",
            }}
          >
            {formatHeatmapValue(point.max_allowable_lw, digits)}
          </span>
        </Html>
      ))}
    </group>
  );
};

const SoundEmission = () => {
  const dispatch = useDispatch();
  const [hoveredPoint, setHoveredPoint] = useState(null);
  const {
    isVisible,
    infoBoxShow,
    selectedPoint,
    lrMax,
    apiLrMax,
    selectedBatchId,
    candidates,
    receivers,
    receiverLinksVisible,
  } = useSelector((state) => state.soundEmission);

  const soundEmissionPoints = useMemo(
    () =>
      candidates.map((point, index) => ({
        ...point,
        id: point.id ?? point.candidate_id ?? `${selectedBatchId}-${index + 1}`,
        x: toNumber(point.x),
        y: toNumber(point.y),
        z: toNumber(point.z),
        di_receiver_x: toNumber(point.di_receiver_x),
        di_receiver_y: toNumber(point.di_receiver_y),
        di_receiver_z: toNumber(point.di_receiver_z),
        lr_max: toNumber(point.lr_max),
        max_allowable_lw: toNumber(point.max_allowable_lw),
      })),
    [candidates, selectedBatchId],
  );
  const soundEmissionReceivers = useMemo(
    () =>
      receivers.map((receiver) => ({
        ...receiver,
        x: toNumber(receiver.x),
        y: toNumber(receiver.y),
        z: toNumber(receiver.z),
      })),
    [receivers],
  );
  const sourceApiLrMax = Number(soundEmissionPoints[0]?.lr_max);

  useEffect(() => {
    if (Number.isFinite(sourceApiLrMax)) {
      dispatch(setSoundEmissionApiLrMax(sourceApiLrMax));
    }
  }, [dispatch, sourceApiLrMax]);

  const adjustedPoints = useMemo(() => {
    const hasLrMax = Number.isFinite(lrMax) && Number.isFinite(apiLrMax);
    const lrDelta = hasLrMax ? lrMax - apiLrMax : 0;
    return soundEmissionPoints.map((point) => ({
      ...point,
      lr_max: hasLrMax ? lrMax : point.lr_max,
      max_allowable_lw: point.max_allowable_lw + lrDelta,
    }));
  }, [apiLrMax, lrMax, soundEmissionPoints]);

  const [colorbarMin, colorbarMax] = useMemo(() => {
    const values = adjustedPoints.map(getAcousticValue);
    if (!values.length) return [0, 0];
    return [Math.min(...values), Math.max(...values)];
  }, [adjustedPoints]);

  useEffect(() => {
    dispatch(setSoundEmissionValueRange([colorbarMin, colorbarMax]));
  }, [colorbarMax, colorbarMin, dispatch]);

  const { min, max, rangeMode } = useColorBar(
    "Sound Emission",
    "Heat pump candidates",
    colorbarMin,
    colorbarMax,
  );

  const displayPoints = useMemo(
    () =>
      filterObjectsByColorbarRange(
        adjustedPoints,
        getAcousticValue,
        min,
        max,
        rangeMode,
      ),
    [adjustedPoints, max, min, rangeMode],
  );

  const setColor = useCallback(
    (point) => {
      const { color } = getRangeModeDisplayColor(point.max_allowable_lw, {
        min,
        max,
        rangeMode,
        colorScale: sensorPlacementScale,
      });
      return new Color(color);
    },
    [max, min, rangeMode],
  );

  const onClick = (event) => {
    const point = displayPoints[event.instanceId];
    if (!point) return;
    dispatch(selectSoundEmissionPoint(point));
    event.stopPropagation();
  };

  const onPointerMove = (event) => {
    const point = displayPoints[event.instanceId];
    if (point) {
      setHoveredPoint((current) =>
        current?.id === point.id ? current : point,
      );
    }
  };

  const highlightedReceiver = useMemo(
    () =>
      getLimitingReceiver(
        hoveredPoint ?? (infoBoxShow ? selectedPoint : null),
        soundEmissionReceivers,
      ),
    [hoveredPoint, infoBoxShow, selectedPoint, soundEmissionReceivers],
  );

  const receiverPressures = useMemo(() => {
    const counts = soundEmissionReceivers.map(() => 0);
    displayPoints.forEach((point) => {
      const receiver = getLimitingReceiver(point, soundEmissionReceivers);
      if (!receiver) return;
      const index = soundEmissionReceivers.indexOf(receiver);
      if (index >= 0) counts[index] += 1;
    });
    return counts;
  }, [displayPoints, soundEmissionReceivers]);

  const highlightedPoints = useMemo(() => {
    const points = [];
    if (hoveredPoint) points.push(hoveredPoint);
    if (infoBoxShow && selectedPoint && selectedPoint.id !== hoveredPoint?.id) {
      points.push(selectedPoint);
    }
    return points;
  }, [hoveredPoint, infoBoxShow, selectedPoint]);

  if (!isVisible) return null;

  return (
    <>
      <SoundEmissionReceivers
        receivers={soundEmissionReceivers}
        highlightedReceiver={highlightedReceiver}
        pressures={receiverLinksVisible ? receiverPressures : null}
      />
      {receiverLinksVisible && (
        <SoundEmissionReceiverLinks
          points={displayPoints}
          highlightedPointId={
            hoveredPoint?.id ?? (infoBoxShow ? selectedPoint?.id : null)
          }
        />
      )}
      <SphereInstancedMesh
        objects={displayPoints}
        setColor={setColor}
        getObjectPosition={getPointPosition}
        groupProps={{
          onClick,
          onPointerMove,
          onPointerOut: () => setHoveredPoint(null),
        }}
        radius={0.4}
        updateDependencyArray={[min, max, rangeMode]}
        highlightProps={{
          highlightedObject: highlightedPoints,
          showHighlight: highlightedPoints.length > 0,
          radius: 0.75,
          getObjectPosition: getPointPosition,
          objectProps: { raycast: disableRaycast },
        }}
      />
      <SoundEmissionValueLabels points={displayPoints} />
    </>
  );
};

export default SoundEmission;
